import { useRef } from "react";

import toArray from "lodash/toArray";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import { SyntheticEvent, useEffect, useState } from "react";

import { Box, Button, Flex, Stack, Text } from "@/storybook-components";
import ProgressBar from "./progress-bar";
import QuestionList from "./question-list";
import { scrollIntoView } from "@/lib";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  paginateBackwards,
  removeSurveyErrors
} from "@/redux/features/survey/slice";
import { saveSurvey } from "@/redux/features/survey/thunk";
import RequiredField from "../questions/required-field";
import Alerts from "../alerts";
import useUser from "@/hooks/useUser";
import InvisibleReCaptcha from "../recaptcha/invisible";
import useLogout from "@/hooks/useLogout";
import usePinned from "@/hooks/usePinned";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";

const SurveyForm = () => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const recaptchaRef = useRef(null);
  const {
    errors,
    answers,
    survey,
    pages,
    questions,
    options,
    saving,
    saveError,
    taken,
    viewResponse
  } = useAppSelector((state) => state.survey);
  const { isRegisteredUserLoggedIn, isUserAdmin, isPreviewUser } = useUser();
  const { profile } = useAppSelector((state) => state.user.registeredUser);
  const { login, roleName } = profile;
  const { logOut } = useLogout();

  const { settings } = useAppSelector((state) => state.settings);
  const { recaptchaSignupEnabled, googleTranslationEnabled } = settings;
  const [currentPageHasRequiredQuestion, setCurrentPageHasRequiredQuestion] =
    useState(false);
  const { isInsideIframe, isEmbed } = useEmbeds();
  const { isPinned } = usePinned();
  const { canContribute, visibilityMode } = useAppSelector(
    (state) => state.project.project
  );

  const {
    state,
    showProgressBar,
    allowUserToPostMultipleTimes,
    allowAnonymousParticipation,
    allowUnverifiedParticipation,
    multipleTimesMsg
  } = survey;
  const currentPage = pages.find((page) => page.current);
  let isReCaptchaEnabled = false;
  // It could be the end of survey in the following scenarios:
  // 1. It's the last page
  // 2. Page triggers the end of survey
  // 3. If page skip doesn't exist & a question skips to the end of survey

  let endOfSurvey = false;
  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage?.id
  );
  for (let i = 0; i < questionsInPage.length; i++) {
    const question = questionsInPage[i];
    if (
      (question.type === "RadioButtonQuestion" ||
        question.type === "DropDownQuestion") &&
      !!answers[question.id]
    ) {
      const option = toArray(options).filter(
        (o) => o.id === answers[question.id].id
      );
      if (option[0]?.triggerEndOfSurvey) {
        endOfSurvey = true;
        break;
      }
    }
  }

  const [
    showAllowUserToPostMultipleTimes,
    setShowAllowUserToPostMultipleTimes
  ] = useState(
    taken &&
      !isUserAdmin &&
      allowUserToPostMultipleTimes &&
      !allowAnonymousParticipation &&
      !allowUnverifiedParticipation
  );

  // find out if a page contains any mandatory questions
  useEffect(() => {
    if (
      questionsInPage.filter((question) => question.required && !question?.skip)
        .length > 0
    ) {
      setCurrentPageHasRequiredQuestion(true);
    } else {
      setCurrentPageHasRequiredQuestion(false);
    }
  }, [questionsInPage]);

  // Must run after the new page has painted, otherwise Google Translate only
  // sees the previous page's DOM.
  useEffect(() => {
    googleTranslateInit(isEmbed, googleTranslationEnabled, { force: true });
  }, [currentPage?.id, isEmbed, googleTranslationEnabled]);

  const handlePrevious = async () => {
    if (Object.keys(errors).length) {
      questionsInPage.forEach((question) => {
        if (errors[question.id]) {
          dispatch(
            removeSurveyErrors({
              questionId: question.id
            })
          );
        }
      });
    }

    dispatch(paginateBackwards({ pageId: currentPage.previous }));

    const formField: HTMLElement = document.querySelector(
      ".ehq-surveyContainer form"
    );
    formField?.focus();

    return scrollIntoView(".ehq-surveyContainer");
  };

  const postSurveyResponse = async (e: SyntheticEvent) => {
    e.preventDefault();
    // Store submit button position before submission
    let shouldScrollToTop = false;
    if (
      isInsideIframe &&
      recaptchaSignupEnabled &&
      !!process.env.RECAPTCHA_INVISIBLE_SITE_KEY
    ) {
      const submitButton = e.currentTarget.querySelector(
        ".ehq-surveySubmitButton"
      );
      const surveyContainer = document.querySelector(".ehq-surveyContainer");

      if (submitButton && surveyContainer) {
        const buttonRect = submitButton.getBoundingClientRect();
        const containerRect = surveyContainer.getBoundingClientRect();
        // If submit button is in lower half of viewport or container top is not visible
        shouldScrollToTop =
          buttonRect.top > window.innerHeight * 0.5 || containerRect.top < 0;
      }
    }

    if (shouldScrollToTop) {
      const surveyContainer = document.querySelector(".ehq-surveyContainer");
      if (surveyContainer) {
        surveyContainer.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }

    await dispatch(saveSurvey({ query, recaptchaRef, isReCaptchaEnabled }));
    if (document.querySelector(".ehq-question-error")) {
      const errorField: HTMLElement =
        document.querySelector(
          ".ehq-question-error button, .ehq-question-error input, .ehq-question-error textarea"
        ) ?? document.querySelector(".ehq-question-error label");

      errorField?.focus();
      return scrollIntoView(".ehq-question-error");
    }

    const formField: HTMLElement = document.querySelector(
      ".ehq-surveyContainer form"
    );
    formField?.focus();

    return scrollIntoView(".ehq-surveyContainer");
  };

  const renderFormButtons = () => {
    const isEndOfSurvey =
      currentPage.last || currentPage.triggerEndOfSurvey || endOfSurvey;
    const primaryButtonTestId = isEndOfSurvey
      ? "survey-participant-button-submit"
      : "survey-participant-button-next";

    return (
      <Flex
        mt={3}
        align="center"
        justifyContent={currentPage.first ? "flex-end" : "space-between"}
      >
        {!currentPage.first && (
          <Button
            fontFamily="inherit"
            variant="primary"
            disabled={saving}
            onClick={handlePrevious}
            className="ehq-surveyPreviousButton"
            data-testid="survey-participant-button-previous"
            size={{ base: "lg", lg: "md" }}
          >
            {translate("common.previous")}
          </Button>
        )}

        <Button
          type="submit"
          loading={saving}
          fontFamily="inherit"
          variant="primary"
          className="ehq-surveySubmitButton"
          data-testid={primaryButtonTestId}
          size={{ base: "lg", lg: "md" }}
        >
          {isEndOfSurvey
            ? translate("common.submit")
            : !isRegisteredUserLoggedIn || (isUserAdmin && state === "draft")
              ? translate("common.next")
              : translate("common.saveAndContinue")}
        </Button>
      </Flex>
    );
  };
  const loggedInUserNode = () => {
    if (!login || viewResponse || !currentPage?.first) return null;

    return (
      <Box>
        <Text
          fontSize="0.75rem"
          lineHeight="133%"
          fontWeight="normal"
          letterSpacing="0.2px"
          color="gray.600"
        >
          {translate("survey.submittingSurveyAs")} <b>{login}</b>,&nbsp;
          {translate("survey.toChange")}&nbsp;
          <Button
            onClick={() => logOut()}
            variant="link"
            data-testid="survey-logoutButton"
            fontSize="xs"
          >
            {translate("common.logout")}
          </Button>
          &nbsp;
        </Text>
      </Box>
    );
  };

  const renderRecaptcha = () => {
    const shouldShowRecaptcha = isRegisteredUserLoggedIn
      ? roleName === "participant"
        ? !!currentPage?.first
        : false
      : currentPage?.last || currentPage?.triggerEndOfSurvey || endOfSurvey;

    isReCaptchaEnabled =
      shouldShowRecaptcha &&
      recaptchaSignupEnabled &&
      !!process.env.RECAPTCHA_INVISIBLE_SITE_KEY;

    if (!isReCaptchaEnabled) return null;

    if (isInsideIframe || isPinned) {
      return (
        <Flex justifyContent="right">
          <InvisibleReCaptcha
            recaptchaRef={recaptchaRef}
            badge="inline"
            hideOtherBadges
          />
        </Flex>
      );
    }

    return <InvisibleReCaptcha recaptchaRef={recaptchaRef} hideOtherBadges />;
  };

  return (
    <Stack
      className="ehq-surveyFormContainer"
      gap={12}
      data-testid="survey-participant-form-root"
    >
      <Stack gap={4}>
        {showAllowUserToPostMultipleTimes && (
          <Alerts
            status="info"
            description={
              multipleTimesMsg || translate("survey.alreadyTakenPartInSurvey")
            }
            onClose={() => setShowAllowUserToPostMultipleTimes(false)}
            data-testid="survey-participant-multiple-times-alert"
          />
        )}
        {loggedInUserNode()}
        {showProgressBar && <ProgressBar />}

        {currentPageHasRequiredQuestion && <RequiredField mt={0} />}
      </Stack>

      {!currentPage ? null : (
        <Box
          as="form"
          noValidate
          tabIndex={-1}
          outline="none"
          onSubmit={postSurveyResponse}
          aria-label={translate("survey.takeSurvey")}
          data-testid="survey-participant-survey-form"
        >
          {/* Remount per page: Google Translate rewrites text nodes React would otherwise patch. */}
          <Stack
            key={currentPage.id}
            as="fieldset"
            gap={12}
            disabled={
              visibilityMode === "Panelled" && (!canContribute || isPreviewUser)
            }
          >
            <QuestionList />
            {saveError && (
              <Alerts status="error" title={translate("survey.saveError")} />
            )}
            {renderFormButtons()}
            {renderRecaptcha()}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default SurveyForm;
